import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { InputField } from '../components/InputField';
import { signUpUser } from '../lib/auth';
import './SignupPage.css';

export const SignupPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<'customer' | 'technician'>('customer');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSignup = async () => {
    // Basic validation
    if (!fullName.trim() || !email.trim() || !password.trim() || !phone.trim()) {
      setError('Please fill in all required fields.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await signUpUser(email, password, role, fullName, phone);
      alert('Account created! Please check your email to confirm your account before signing in.');
      navigate('/login');
    } catch (err: any) {
      setError(`Signup failed: ${err.message}`);
    }
    
    setLoading(false);
  };

  return (
    <div className="signup-page">
      <div className="signup-container">
        <div className="signup-header">
          <Link to="/" className="back-button">
            <i className="fas fa-arrow-left"></i>
          </Link>
          <h1 className="signup-title">Create Account</h1>
          <p className="signup-subtitle">Join On My Way Auto</p>
        </div>

        <div className="signup-form">
          {error && (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              <span>{error}</span>
            </div>
          )}

          <div className="role-section">
            <h3 className="section-title">Account Type</h3>
            <div className="role-container">
              <button
                type="button"
                className={`role-button ${role === 'customer' ? 'role-button-selected' : ''}`}
                onClick={() => setRole('customer')}
              >
                <i className="fas fa-user"></i>
                <span className="role-text">Customer</span>
              </button>
              <button
                type="button"
                className={`role-button ${role === 'technician' ? 'role-button-selected' : ''}`}
                onClick={() => setRole('technician')}
              >
                <i className="fas fa-tools"></i>
                <span className="role-text">Technician</span>
              </button>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Personal Information</h3>
            <InputField
              label="Full Name *"
              value={fullName}
              onChangeText={setFullName}
              placeholder="Enter your full name"
            />
            <InputField
              label="Phone Number *"
              value={phone}
              onChangeText={setPhone}
              placeholder="Enter your phone number"
              keyboardType="phone-pad"
            />
          </div>

          <div className="form-section">
            <h3 className="section-title">Account Details</h3>
            <InputField
              label="Email Address *"
              value={email}
              onChangeText={setEmail}
              placeholder="Enter your email address"
              keyboardType="email-address"
            />
            <InputField
              label="Password *"
              value={password}
              onChangeText={setPassword}
              placeholder="Create a password (min. 6 characters)"
              secureTextEntry
            />
          </div>

          <div className="submit-container">
            <Button
              title={loading ? 'Creating Account...' : 'Create Account'}
              onPress={handleSignup}
              variant="primary"
              disabled={loading}
            />
          </div>

          <div className="login-container">
            <span className="login-text">Already have an account? </span>
            <Link to="/login" className="login-link">
              Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
